import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Check, X, Plus, Download, Upload, FileSpreadsheet, CalendarOff, Loader2, AlertTriangle, Award } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Status palette · expanded Iter 55 (manual marking by HR, leave-types stitched in)
const STATUS_COLORS = {
  P: 'bg-emerald-500 text-white',
  H: 'bg-amber-400 text-slate-900',
  A: 'bg-red-500 text-white',
  WFH: 'bg-teal-500 text-white',
  WO: 'bg-slate-300 text-slate-700',
  HO: 'bg-teal-200 text-teal-900',
  L: 'bg-violet-500 text-white',
  PL: 'bg-violet-600 text-white',
  SL: 'bg-pink-500 text-white',
  CL: 'bg-purple-500 text-white',
  SPL: 'bg-fuchsia-400 text-white',
  ML: 'bg-rose-500 text-white',
  LWP: 'bg-zinc-700 text-white',
};
const STATUS_LABELS = {
  P: 'Present',
  H: 'Half Day',
  A: 'Absent',
  WFH: 'Work From Home',
  WO: 'Weekly Off',
  HO: 'Holiday',
  L: 'Leave (legacy)',
  PL: 'Privilege Leave',
  SL: 'Sick Leave',
  CL: 'Casual Leave',
  SPL: 'Short Permission',
  ML: 'Maternity Leave',
  LWP: 'Leave Without Pay',
};
const STATUS_ORDER = ['P', 'H', 'A', 'WFH', 'WO', 'HO', 'PL', 'SL', 'CL', 'SPL', 'ML', 'LWP'];

function formatYM(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function AttendanceLeave() {
  const [tab, setTab] = useState('attendance');
  return (
    <div className="space-y-4" data-testid="attendance-leave">
      <div className="bg-teal-50 border border-teal-200 rounded-sm p-3 text-xs text-teal-900">
        <strong>HR Manual Marking:</strong> Click any cell to mark the day. Codes P/H/A/WFH/WO/HO are direct status; PL/SL/CL/SPL/ML/LWP auto-create an Approved leave record and decrement the employee&apos;s balance. Mobile self-service punch will replace this when launched.
      </div>
      <div className="flex gap-2 border-b border-slate-200">
        {[
          { id: 'attendance', label: 'Attendance Roster' },
          { id: 'leaves', label: 'Leave Workflow' },
          { id: 'compoff', label: 'Comp-Off Ledger' },
        ].map(t => (
          <button
            key={t.id}
            data-testid={`subtab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id ? 'text-[#1F7A6E] border-[#1F7A6E]' : 'text-slate-500 border-transparent hover:text-slate-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'attendance' && <AttendanceMatrix />}
      {tab === 'leaves' && <LeaveWorkflow />}
      {tab === 'compoff' && <CompOffLedger />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Comp-Off Ledger — who earned compensatory offs (Sundays/holidays worked),
// their available balance, and a liability alert for high accumulations.
// ════════════════════════════════════════════════════════════════════════
function CompOffLedger() {
  const [summary, setSummary] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        fetch(`${API}/api/hr/comp-off/summary`, { credentials: 'include' }).then(r => r.json()).catch(() => null),
        fetch(`${API}/api/hr/comp-off`, { credentials: 'include' }).then(r => r.json()).catch(() => ({ rows: [] })),
      ]);
      setSummary(s);
      setLedger(Array.isArray(l?.rows) ? l.rows : []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = summary?.rows || [];

  return (
    <Card data-testid="compoff-ledger">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500 flex items-center gap-2">
          <Award size={15} className="text-[#1F7A6E]" /> Compensatory Off Ledger
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          <div className="border border-slate-200 rounded-md p-3 bg-slate-50/50">
            <div className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Total Available</div>
            <div className="font-display font-extrabold text-2xl mt-1 tabular-nums" data-testid="compoff-total-available">{summary?.total_available ?? 0}<span className="text-xs text-slate-400 ml-1">days</span></div>
          </div>
          <div className="border border-slate-200 rounded-md p-3 bg-slate-50/50">
            <div className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Employees w/ Balance</div>
            <div className="font-display font-extrabold text-2xl mt-1 tabular-nums">{summary?.employees_with_balance ?? 0}</div>
          </div>
          <div className={`border rounded-md p-3 ${summary?.high_balance_count ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50/50'}`}>
            <div className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">High Balance (≥{summary?.high_threshold ?? 5})</div>
            <div className="font-display font-extrabold text-2xl mt-1 tabular-nums text-amber-700">{summary?.high_balance_count ?? 0}</div>
          </div>
        </div>

        {summary?.high_balance_count > 0 && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 mb-4" data-testid="compoff-alert">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span><strong>{summary.high_balance_count} employee{summary.high_balance_count > 1 ? 's have' : ' has'}</strong> ≥ {summary.high_threshold} unused comp-offs — a growing leave liability. Encourage them to avail comp-offs or settle as per policy.</span>
          </div>
        )}

        {loading ? (
          <div className="text-center py-8 text-slate-400 text-sm"><Loader2 size={16} className="inline animate-spin mr-2" />Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-sm italic">No comp-offs earned yet. They accrue when an employee is Present on a Sunday or public holiday.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Emp Code</th>
                  <th className="text-left py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Name</th>
                  <th className="text-right py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Earned</th>
                  <th className="text-right py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Used</th>
                  <th className="text-right py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Available</th>
                  <th className="text-left py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Last Earned</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.employee_id || i} className={`${i % 2 ? 'bg-slate-50/40' : ''} ${r.high_balance ? 'bg-amber-50/60' : ''}`} data-testid={`compoff-row-${r.emp_code}`}>
                    <td className="py-2 px-2 font-mono">{r.emp_code || '—'}</td>
                    <td className="py-2 px-2 font-display font-semibold">{r.employee_name || '—'}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.earned}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-slate-500">{r.used}</td>
                    <td className="py-2 px-2 text-right tabular-nums font-display font-bold">
                      {r.available}
                      {r.high_balance && <AlertTriangle size={11} className="inline ml-1 text-amber-600" />}
                    </td>
                    <td className="py-2 px-2 tabular-nums text-slate-500">{r.last_earned_date || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Recent credit detail */}
        {ledger.length > 0 && (
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500 mb-2">Recent Credits · {ledger.length}</div>
            <div className="flex flex-wrap gap-2">
              {ledger.slice(0, 40).map((c) => (
                <span key={c.id} className="inline-flex items-center gap-1.5 text-[11px] border border-slate-200 rounded-full px-2.5 py-1 bg-white">
                  <span className="font-mono text-slate-500">{c.date}</span>
                  <span className="font-display font-semibold">{c.employee_name}</span>
                  <span className="text-emerald-700 font-bold">+{c.credit}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AttendanceMatrix() {
  const [month, setMonth] = useState(formatYM(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [picker, setPicker] = useState(null); // { empId, date, anchorRect }
  const [ioBusy, setIoBusy] = useState(false);
  const [showImportWizard, setShowImportWizard] = useState(false);
  // Attendance filters: by Department, Project, Work Location
  const [fDept, setFDept] = useState('');
  const [fProj, setFProj] = useState('');
  const [fLoc, setFLoc] = useState('');
  const [fQuery, setFQuery] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/hr/attendance?month=${month}`, { credentials: 'include' });
      const d = await r.json();
      setData(d);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const shift = (dir) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setMonth(formatYM(d));
  };

  const SUMMARY_BUCKET = (st) => {
    if (!st) return null;
    if (st === 'P' || st === 'A' || st === 'H') return st;
    if (['L', 'PL', 'SL', 'CL', 'SPL', 'ML', 'LWP'].includes(st)) return 'TOTAL_LEAVE';
    return null;
  };

  const applyLocalCellUpdate = (empId, date, newStatus) => {
    setData(prev => {
      if (!prev) return prev;
      const oldStatus = prev.matrix?.[empId]?.[date];
      const newMatrix = { ...(prev.matrix || {}), [empId]: { ...(prev.matrix?.[empId] || {}) } };
      if (newStatus === null) {
        delete newMatrix[empId][date];
      } else {
        newMatrix[empId][date] = newStatus;
      }
      // Update row summary
      const oldBucket = SUMMARY_BUCKET(oldStatus);
      const newBucket = SUMMARY_BUCKET(newStatus);
      const rowSummary = { ...(prev.summary?.[empId] || {}) };
      if (oldBucket && oldBucket !== newBucket) {
        rowSummary[oldBucket] = Math.max(0, (rowSummary[oldBucket] || 0) - 1);
      }
      if (newBucket && newBucket !== oldBucket) {
        rowSummary[newBucket] = (rowSummary[newBucket] || 0) + 1;
      }
      return {
        ...prev,
        matrix: newMatrix,
        summary: { ...(prev.summary || {}), [empId]: rowSummary },
      };
    });
  };

  const markCell = async (empId, date, status) => {
    // Optimistic: update UI immediately, rollback on failure
    const prevStatus = data?.matrix?.[empId]?.[date];
    applyLocalCellUpdate(empId, date, status);
    setPicker(null);
    try {
      const r = await fetch(`${API}/api/hr/attendance/mark`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employee_id: empId, date, status }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Failed to mark');
        applyLocalCellUpdate(empId, date, prevStatus ?? null);  // rollback
        return;
      }
      const out = await r.json().catch(() => ({}));
      if (out.leave_record_created) {
        toast.success(`${STATUS_LABELS[status]} marked · leave record created`);
      } else {
        toast.success(`${STATUS_LABELS[status]} marked`);
      }
    } catch {
      toast.error('Network error');
      applyLocalCellUpdate(empId, date, prevStatus ?? null);  // rollback
    }
  };

  const clearCell = async (empId, date) => {
    const prevStatus = data?.matrix?.[empId]?.[date];
    applyLocalCellUpdate(empId, date, null);
    setPicker(null);
    try {
      const r = await fetch(
        `${API}/api/hr/attendance/clear?employee_id=${encodeURIComponent(empId)}&date=${encodeURIComponent(date)}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Failed to clear');
        applyLocalCellUpdate(empId, date, prevStatus ?? null);  // rollback
        return;
      }
      const out = await r.json().catch(() => ({}));
      toast.success(
        out.leave_records_removed
          ? `Attendance cleared · ${out.leave_records_removed} leave record(s) reverted`
          : 'Attendance cleared'
      );
    } catch {
      toast.error('Network error');
      applyLocalCellUpdate(empId, date, prevStatus ?? null);  // rollback
    }
  };

  // derive distinct filter options + apply filters
  const allEmps = data?.employees || [];
  const depts = Array.from(new Set(allEmps.map(e => e.department).filter(Boolean))).sort();
  const projs = Array.from(
    new Map(
      allEmps
        .filter(e => e.project_id)
        .map(e => [e.project_id, { id: e.project_id, name: e.project_name || e.project_id }])
    ).values()
  ).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const locs = Array.from(new Set(allEmps.map(e => e.work_location).filter(Boolean))).sort();
  const filteredEmps = allEmps.filter(e => {
    if (fDept && (e.department || '') !== fDept) return false;
    if (fProj && (e.project_id || '') !== fProj) return false;
    if (fLoc && (e.work_location || '') !== fLoc) return false;
    if (fQuery) {
      const q = fQuery.toLowerCase();
      const hay = `${e.name || ''} ${e.emp_id || ''} ${e.designation || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  const clearFilters = () => { setFDept(''); setFProj(''); setFLoc(''); setFQuery(''); };
  const hasActiveFilter = !!(fDept || fProj || fLoc || fQuery);

  // ── Excel import / export / template / auto weekly-off ──────────────────
  const downloadXlsx = async (path, filename) => {
    setIoBusy(true);
    try {
      const r = await fetch(`${API}${path}`, { credentials: 'include' });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || `Download failed (${r.status})`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || 'Download failed');
    } finally {
      setIoBusy(false);
    }
  };

  const downloadTemplate = () => downloadXlsx(`/api/hr/attendance/template?month=${month}`, `attendance-template-${month}.xlsx`);
  const exportXlsx = () => downloadXlsx(`/api/hr/attendance/export?month=${month}`, `attendance-${month}.xlsx`);

  const autoWeeklyOff = async () => {
    setIoBusy(true);
    try {
      const r = await fetch(`${API}/api/hr/attendance/auto-weekly-off?month=${month}`, { method: 'POST', credentials: 'include' });
      const out = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(out.detail || 'Failed');
      toast.success(`Marked ${out.weekly_offs_marked} Sunday weekly-off cell${out.weekly_offs_marked === 1 ? '' : 's'}`);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Auto weekly-off failed');
    } finally {
      setIoBusy(false);
    }
  };

  const onWizardImportSuccess = useCallback((result) => {
    // Refresh the roster if the imported month is the one currently displayed
    if (result.month === month) fetchData();
  }, [month, fetchData]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500">
            Attendance Roster
          </CardTitle>
          <div className="flex items-center gap-2">
            <button onClick={() => shift(-1)} className="p-1.5 border rounded-md hover:bg-slate-50" data-testid="att-prev"><ChevronLeft size={14} /></button>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              data-testid="att-month"
              className="border border-slate-200 rounded-md px-3 py-1.5 text-sm"
            />
            <button onClick={() => shift(1)} className="p-1.5 border rounded-md hover:bg-slate-50" data-testid="att-next"><ChevronRight size={14} /></button>
            <button
              onClick={downloadTemplate}
              disabled={ioBusy}
              data-testid="att-download-template"
              className="ml-2 text-xs px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-50"
              title={`Download a blank Excel template for ${month} (employees × days). The month is stamped into the sheet's top row.`}
            >
              <FileSpreadsheet size={12} /> Template
            </button>
            <button
              onClick={() => setShowImportWizard(true)}
              disabled={ioBusy}
              data-testid="att-import-xlsx"
              className="text-xs px-3 py-1.5 rounded-md border border-[#1F7A6E] text-[#1F7A6E] hover:bg-teal-50 flex items-center gap-1.5 disabled:opacity-50"
              title="Import attendance from an Excel file — opens the guided import wizard with preview and error visibility."
            >
              <Upload size={12} /> Import
            </button>
            <button
              onClick={exportXlsx}
              disabled={ioBusy}
              data-testid="att-export-xlsx"
              className="text-xs px-3 py-1.5 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 flex items-center gap-1.5 disabled:opacity-50"
              title="Export this month's attendance to Excel"
            >
              <Download size={12} /> Export
            </button>
            <button
              onClick={autoWeeklyOff}
              disabled={ioBusy}
              data-testid="att-auto-weekly-off"
              className="text-xs px-3 py-1.5 rounded-md border border-amber-300 text-amber-700 hover:bg-amber-50 flex items-center gap-1.5 disabled:opacity-50"
              title="Auto-mark every Sunday as Weekly Off (only empty cells)"
            >
              <CalendarOff size={12} /> Auto WO
            </button>
          </div>
        </div>
        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-2 pt-3" data-testid="att-filter-row">
          <input
            type="text"
            value={fQuery}
            onChange={(e) => setFQuery(e.target.value)}
            placeholder="Search name / emp-id / designation…"
            className="border border-slate-200 rounded-md px-3 py-1.5 text-xs w-56 focus:outline-none focus:border-[#1F7A6E]"
            data-testid="att-filter-search"
          />
          <select
            value={fDept}
            onChange={(e) => setFDept(e.target.value)}
            className="border border-slate-200 rounded-md px-3 py-1.5 text-xs bg-white"
            data-testid="att-filter-department"
          >
            <option value="">All Departments ({depts.length})</option>
            {depts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select
            value={fProj}
            onChange={(e) => setFProj(e.target.value)}
            className="border border-slate-200 rounded-md px-3 py-1.5 text-xs bg-white max-w-[260px]"
            data-testid="att-filter-project"
          >
            <option value="">All Projects ({projs.length})</option>
            {projs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select
            value={fLoc}
            onChange={(e) => setFLoc(e.target.value)}
            className="border border-slate-200 rounded-md px-3 py-1.5 text-xs bg-white"
            data-testid="att-filter-location"
          >
            <option value="">All Locations ({locs.length})</option>
            {locs.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          {hasActiveFilter && (
            <button
              onClick={clearFilters}
              data-testid="att-filter-clear"
              className="text-[11px] text-slate-500 hover:text-rose-600 underline ml-1"
            >
              Clear filters
            </button>
          )}
          <span className="text-[11px] text-slate-500 ml-auto" data-testid="att-filter-count">
            <span className="font-semibold text-slate-700">{filteredEmps.length}</span> of {allEmps.length} employees
          </span>
        </div>
        <Legend />
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading…</div>
        ) : !data || data.employees.length === 0 ? (
          <div className="text-center py-12 text-slate-500">No employees.</div>
        ) : filteredEmps.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            No employees match the current filters.
            <button onClick={clearFilters} className="ml-2 text-[#1F7A6E] underline">Clear filters</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="text-xs border-collapse">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-display uppercase tracking-[0.14em] text-[10px] text-slate-500 sticky left-0 bg-slate-50 z-10 min-w-[180px] border-r border-slate-200">Employee</th>
                  {data.days.map(d => {
                    const dt = new Date(d);
                    const isWeekend = dt.getDay() === 0 || dt.getDay() === 6;
                    return (
                      <th key={d} className={`px-1 py-2 font-mono text-[10px] text-center min-w-[26px] ${isWeekend ? 'text-slate-400' : 'text-slate-600'}`}>
                        {String(dt.getDate()).padStart(2, '0')}
                      </th>
                    );
                  })}
                  <th className="px-2 py-2 text-center font-display uppercase tracking-[0.14em] text-[10px] text-slate-500 border-l border-slate-200" colSpan={4}>Summary</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmps.map(emp => (
                  <MatrixRow
                    key={emp.id}
                    emp={emp}
                    days={data.days}
                    rowData={data.matrix?.[emp.id]}
                    summary={data.summary[emp.id]}
                    onPick={(date, rect) => setPicker({ empId: emp.id, date, rect })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {picker && (
          <StatusPicker
            employee={data.employees.find(e => e.id === picker.empId)}
            date={picker.date}
            rect={picker.rect}
            current={data.matrix?.[picker.empId]?.[picker.date]}
            onPick={(st) => markCell(picker.empId, picker.date, st)}
            onClear={() => clearCell(picker.empId, picker.date)}
            onClose={() => setPicker(null)}
          />
        )}
      </CardContent>
      {showImportWizard && (
        <AttendanceImportWizard
          isOpen={showImportWizard}
          onClose={() => setShowImportWizard(false)}
          currentMonth={month}
          onImportSuccess={onWizardImportSuccess}
        />
      )}
    </Card>
  );
}

const MatrixRow = React.memo(function MatrixRow({ emp, days, rowData, summary, onPick }) {
  const s = summary || {};
  return (
    <tr className="border-b border-slate-100 hover:bg-teal-50/30" data-testid={`att-row-${emp.emp_id}`}>
      <td className="px-3 py-1.5 sticky left-0 bg-white hover:bg-teal-50/30 border-r border-slate-200">
        <div className="font-medium text-slate-800">{emp.name}</div>
        <div className="text-[10px] text-slate-500">{emp.emp_id} · {emp.designation || '—'}</div>
      </td>
      {days.map(d => {
        const dt = new Date(d);
        const isWeekend = dt.getDay() === 0 || dt.getDay() === 6;
        const st = rowData?.[d];
        return (
          <td key={d} className="text-center px-0.5 py-1.5">
            <button
              type="button"
              data-testid={`cell-${emp.emp_id}-${d}`}
              title={st ? STATUS_LABELS[st] : (isWeekend ? 'Weekend · click to mark' : 'Click to mark')}
              onClick={(e) => onPick(d, e.currentTarget.getBoundingClientRect())}
              className={`w-6 h-6 inline-flex items-center justify-center rounded-sm text-[10px] font-bold cursor-pointer transition-transform hover:scale-110 ${
                st ? STATUS_COLORS[st] : isWeekend ? 'bg-slate-100 text-slate-300' : 'bg-slate-50 text-slate-400'
              }`}
            >
              {st || '·'}
            </button>
          </td>
        );
      })}
      <td className="px-2 text-center font-bold text-emerald-600 border-l border-slate-200">{s.P || 0}</td>
      <td className="px-2 text-center font-bold text-red-600">{s.A || 0}</td>
      <td className="px-2 text-center font-bold text-amber-600">{s.H || 0}</td>
      <td className="px-2 text-center font-bold text-violet-600">{s.TOTAL_LEAVE || 0}</td>
    </tr>
  );
});

function StatusPicker({ employee, date, rect, current, onPick, onClear, onClose }) {
  // Position popover near the clicked cell
  const style = rect ? {
    position: 'fixed',
    top: Math.min(rect.bottom + 6, window.innerHeight - 320),
    left: Math.min(rect.left, window.innerWidth - 280),
    zIndex: 50,
  } : { position: 'fixed', top: 80, left: 80, zIndex: 50 };

  // Close on outside click / Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const onDoc = (e) => {
      if (!e.target.closest('[data-picker-root]')) onClose();
    };
    document.addEventListener('keydown', onKey);
    setTimeout(() => document.addEventListener('mousedown', onDoc), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [onClose]);

  return (
    <div data-picker-root data-testid="status-picker" style={style} className="bg-white border border-slate-200 rounded-md shadow-xl p-3 w-[260px]">
      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Mark Attendance</div>
      <div className="text-xs font-semibold text-slate-800 mb-2">{employee?.name} · <span className="text-slate-500 font-normal">{date}</span></div>
      <div className="grid grid-cols-3 gap-1.5 mb-2">
        {STATUS_ORDER.map(st => (
          <button
            key={st}
            type="button"
            data-testid={`picker-${st}`}
            onClick={() => onPick(st)}
            title={STATUS_LABELS[st]}
            className={`px-2 py-1.5 rounded-sm text-[11px] font-bold border ${current === st ? 'ring-2 ring-[#1F7A6E] ' : 'border-slate-200 hover:border-slate-400'} ${STATUS_COLORS[st]}`}
          >
            <div className="font-mono leading-none">{st}</div>
            <div className="text-[8px] font-medium mt-0.5 opacity-90 truncate">{STATUS_LABELS[st]}</div>
          </button>
        ))}
      </div>
      <button onClick={onClose} className="w-full text-center text-[10px] text-slate-500 hover:text-slate-700 mt-1 py-1">
        Cancel
      </button>
      {current && onClear && (
        <button
          type="button"
          onClick={onClear}
          data-testid="picker-clear"
          className="w-full mt-1 text-center text-[11px] font-bold text-red-600 hover:text-white hover:bg-red-600 border border-red-200 rounded-sm py-1.5 transition-colors"
          title="Clear this attendance entry (undo accidental mark)"
        >
          Clear ({current})
        </button>
      )}
    </div>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap gap-2 text-[10px] text-slate-600 mt-2">
      <span className="text-slate-500 mr-2 font-semibold">Click any cell to mark:</span>
      {STATUS_ORDER.map((k) => (
        <div key={k} className="flex items-center gap-1">
          <span className={`w-4 h-4 rounded-sm flex items-center justify-center text-[9px] font-bold ${STATUS_COLORS[k]}`}>{k}</span>
          <span>{STATUS_LABELS[k]}</span>
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Attendance Import Wizard — multi-step dialog
// Step 0: Upload file + pick month
// Step 1: Preview results (matches, errors, conflicts) + configure options
// Step 2: Done — show result stats
// ════════════════════════════════════════════════════════════════════════
function AttendanceImportWizard({ isOpen, onClose, currentMonth, onImportSuccess }) {
  const STEPS = ['Upload', 'Preview', 'Done'];
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [month, setMonth] = useState(currentMonth);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [conflictMode, setConflictMode] = useState('update');
  const [autoWO, setAutoWO] = useState(true);
  const [importResult, setImportResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setStep(0);
      setFile(null);
      setMonth(currentMonth);
      setPreview(null);
      setImportResult(null);
    }
  }, [isOpen, currentMonth]);

  const handleFile = (f) => { if (f) setFile(f); };

  const analyzeFile = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API}/api/hr/attendance/preview`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Preview failed');
      setPreview(data);
      if (data.detected_month) setMonth(data.detected_month);
      setStep(1);
    } catch (err) {
      toast.error(err.message || 'Failed to analyze file');
    } finally {
      setLoading(false);
    }
  };

  const doImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const params = new URLSearchParams({
        month,
        conflict_mode: conflictMode,
        auto_weekly_off: autoWO ? 'true' : 'false',
      });
      const r = await fetch(`${API}/api/hr/attendance/import?${params}`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const result = await r.json();
      if (!r.ok) throw new Error(result.detail || 'Import failed');
      setImportResult(result);
      setStep(2);
      onImportSuccess(result);
    } catch (err) {
      toast.error(err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  if (!isOpen) return null;

  const summary = preview?.summary || {};
  const unmatchedCount = summary.unmatched_emp_codes?.length || 0;
  const invalidCount = summary.invalid_cells || 0;
  const conflictCount = summary.conflict_cells || 0;

  // Flatten invalid values from all preview rows for the error table
  const allInvalidCells = (preview?.rows || [])
    .filter(r => r.invalid_values?.length > 0)
    .flatMap(r => r.invalid_values.map(iv => ({ emp_code: r.emp_code, ...iv })));

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="import-wizard">
      <div
        className="bg-white rounded-lg w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between border-b px-6 py-4 shrink-0">
          <div>
            <h2 className="font-display font-bold text-lg text-slate-800">Import Attendance</h2>
            <div className="flex items-center gap-1.5 mt-2">
              {STEPS.map((s, i) => (
                <React.Fragment key={s}>
                  <span
                    className={`text-[11px] font-medium px-2.5 py-0.5 rounded-full transition-colors ${
                      i === step ? 'bg-[#1F7A6E] text-white' :
                      i < step  ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'
                    }`}
                    data-testid={`wizard-step-${i}`}
                  >
                    {i < step && <Check size={9} className="inline mr-0.5 -mt-0.5" />}{s}
                  </span>
                  {i < STEPS.length - 1 && <span className="text-slate-300 text-xs">→</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1 rounded"
            data-testid="wizard-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* ─── Step 0: Upload ─── */}
          {step === 0 && (
            <>
              {/* Drop zone */}
              <div
                data-testid="wizard-dropzone"
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => {
                  e.preventDefault(); setDragOver(false);
                  const f = e.dataTransfer.files[0]; if (f) handleFile(f);
                }}
                onClick={() => fileRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  dragOver ? 'border-[#1F7A6E] bg-teal-50' : 'border-slate-300 hover:border-[#1F7A6E] hover:bg-slate-50'
                }`}
              >
                <Upload size={28} className="mx-auto text-slate-400 mb-3" />
                {file ? (
                  <div>
                    <div className="font-medium text-slate-800 text-sm">{file.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{(file.size / 1024).toFixed(0)} KB · Click or drop to change</div>
                  </div>
                ) : (
                  <div>
                    <div className="text-sm text-slate-600 font-medium">Drop an Excel file here, or click to browse</div>
                    <div className="text-xs text-slate-400 mt-1">.xlsx · .xls · .xlsm · .csv · max 10 MB</div>
                  </div>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.xls,.xlsm,.csv"
                  className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
                  data-testid="wizard-file-input"
                />
              </div>

              {/* Month selector */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-600 block">
                  Target Month
                  <span className="text-slate-400 font-normal ml-1">(you can override after preview if the file says otherwise)</span>
                </label>
                <input
                  type="month"
                  value={month}
                  onChange={e => setMonth(e.target.value)}
                  className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F7A6E]/40"
                  data-testid="wizard-month"
                />
              </div>

              <div className="text-xs text-slate-400 bg-slate-50 rounded-md p-3 leading-relaxed border border-slate-200">
                <strong className="text-slate-500">Tip:</strong> Use the <strong>Template</strong> button on the roster to download a pre-filled sheet for the correct month. The wizard will show you a full preview before writing any data.
              </div>
            </>
          )}

          {/* ─── Step 1: Preview & Configure ─── */}
          {step === 1 && preview && (
            <>
              {/* Month row + mismatch warning */}
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-600 block">Import into Month</label>
                  <input
                    type="month"
                    value={month}
                    onChange={e => setMonth(e.target.value)}
                    className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F7A6E]/40"
                    data-testid="wizard-month-step2"
                  />
                </div>
                {preview.detected_month && preview.detected_month !== month && (
                  <div
                    className="flex items-center gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
                    data-testid="wizard-month-mismatch"
                  >
                    <AlertTriangle size={12} className="shrink-0" />
                    File says <strong className="mx-1">{preview.detected_month}</strong> — wizard will use <strong className="ml-1">{month}</strong>.
                  </div>
                )}
                {preview.detected_month && preview.detected_month === month && (
                  <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="wizard-month-match">
                    Month matches file (<strong>{month}</strong>).
                  </div>
                )}
                {!preview.detected_month && (
                  <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                    No month detected in file — using your selection (<strong>{month}</strong>).
                  </div>
                )}
              </div>

              {/* Header-not-found error */}
              {!preview.header_found && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-300 rounded-md p-4 text-xs text-red-900" data-testid="wizard-no-header">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5 text-red-600" />
                  <div>
                    <strong className="block mb-1">Emp Code header row not found.</strong>
                    The importer looks for a column named &ldquo;Emp Code&rdquo;, &ldquo;Emp_ID&rdquo;, or &ldquo;Employee Code&rdquo;.
                    Use the <strong>Template</strong> button on the roster to download the correct format.
                    {preview.raw_headers?.length > 0 && (
                      <div className="mt-1.5 text-red-700">
                        <span className="font-semibold">Columns detected: </span>
                        {preview.raw_headers.slice(0, 12).filter(Boolean).join(', ')}
                        {preview.raw_headers.length > 12 && ` …and ${preview.raw_headers.length - 12} more`}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Summary stat cards */}
              {preview.header_found && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="border border-emerald-200 bg-emerald-50 rounded-md p-3 text-center" data-testid="wizard-stat-matched">
                    <div className="text-2xl font-display font-extrabold text-emerald-700">{summary.matched_employees || 0}</div>
                    <div className="text-[10px] uppercase tracking-widest text-emerald-600 mt-0.5">Matched</div>
                  </div>
                  <div className={`border rounded-md p-3 text-center ${unmatchedCount > 0 ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-slate-50'}`} data-testid="wizard-stat-unmatched">
                    <div className={`text-2xl font-display font-extrabold ${unmatchedCount > 0 ? 'text-amber-700' : 'text-slate-400'}`}>{unmatchedCount}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Unmatched</div>
                  </div>
                  <div className="border border-slate-200 bg-slate-50 rounded-md p-3 text-center" data-testid="wizard-stat-valid">
                    <div className="text-2xl font-display font-extrabold text-slate-700">{summary.valid_cells || 0}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Valid Cells</div>
                  </div>
                  <div className={`border rounded-md p-3 text-center ${invalidCount > 0 ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'}`} data-testid="wizard-stat-invalid">
                    <div className={`text-2xl font-display font-extrabold ${invalidCount > 0 ? 'text-red-600' : 'text-slate-400'}`}>{invalidCount}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Invalid Cells</div>
                  </div>
                </div>
              )}

              {/* Conflicts banner */}
              {conflictCount > 0 && (
                <div className="flex items-center gap-2 text-xs text-teal-800 bg-teal-50 border border-teal-200 rounded-md px-3 py-2" data-testid="wizard-conflicts">
                  <AlertTriangle size={12} className="shrink-0" />
                  <span>
                    <strong>{conflictCount}</strong> cells already have records for <strong>{month}</strong>.
                    Choose &ldquo;Update&rdquo; to overwrite or &ldquo;Skip&rdquo; to preserve existing data below.
                  </span>
                </div>
              )}

              {/* Unmatched codes */}
              {unmatchedCount > 0 && (
                <div data-testid="wizard-unmatched">
                  <div className="text-xs font-semibold text-amber-700 mb-1.5">
                    Unmatched Emp Codes — {unmatchedCount} row{unmatchedCount > 1 ? 's' : ''} will be skipped:
                  </div>
                  <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto p-2 border border-amber-200 rounded-md bg-amber-50">
                    {summary.unmatched_emp_codes.map(c => (
                      <span key={c} className="inline-flex text-[11px] font-mono bg-amber-100 text-amber-800 border border-amber-300 rounded px-2 py-0.5">{c}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Invalid cell values */}
              {invalidCount > 0 && (
                <div data-testid="wizard-invalid-cells">
                  <div className="text-xs font-semibold text-red-700 mb-1.5">
                    Invalid Values — {invalidCount} cell{invalidCount > 1 ? 's' : ''} will be skipped during import:
                  </div>
                  <div className="border border-red-200 rounded-md overflow-hidden">
                    <div className="overflow-y-auto max-h-44">
                      <table className="w-full text-xs">
                        <thead className="bg-red-100 border-b border-red-200 sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-widest text-red-700">Emp Code</th>
                            <th className="text-center px-3 py-1.5 text-[10px] uppercase tracking-widest text-red-700">Day</th>
                            <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-widest text-red-700">Value Found</th>
                          </tr>
                        </thead>
                        <tbody className="bg-red-50">
                          {allInvalidCells.slice(0, 60).map((iv, i) => (
                            <tr key={i} className="border-b border-red-100 last:border-0">
                              <td className="px-3 py-1.5 font-mono text-red-700">{iv.emp_code}</td>
                              <td className="px-3 py-1.5 text-center text-slate-600">{iv.day}</td>
                              <td className="px-3 py-1.5 font-mono font-bold text-red-600">{iv.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1">
                    Valid codes: <span className="font-mono">{preview.valid_codes?.join(' · ')}</span>
                    {allInvalidCells.length > 60 && ` · showing first 60 of ${allInvalidCells.length}`}
                  </div>
                </div>
              )}

              {/* Import options */}
              {preview.header_found && (
                <div className="border border-slate-200 rounded-md p-4 bg-slate-50/60 space-y-3">
                  <div className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Import Options</div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-slate-600 w-36 shrink-0">Existing records:</span>
                    <button
                      onClick={() => setConflictMode('update')}
                      data-testid="wizard-opt-update"
                      className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${conflictMode === 'update' ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-100'}`}
                    >
                      Update (overwrite)
                    </button>
                    <button
                      onClick={() => setConflictMode('skip')}
                      data-testid="wizard-opt-skip"
                      className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${conflictMode === 'skip' ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-100'}`}
                    >
                      Skip (keep existing)
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-slate-600 w-36 shrink-0">Blank Sundays:</span>
                    <button
                      onClick={() => setAutoWO(!autoWO)}
                      data-testid="wizard-opt-auto-wo"
                      className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${autoWO ? 'bg-amber-100 text-amber-700 border-amber-300' : 'bg-white border-slate-300 text-slate-500 hover:bg-slate-100'}`}
                    >
                      {autoWO ? 'Auto-fill as WO (on)' : 'Leave blank (off)'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Step 2: Done */}
          {step === 2 && importResult && (
            <div className="space-y-4" data-testid="wizard-done">
              <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <Check size={22} className="text-emerald-600 shrink-0" />
                <div>
                  <div className="font-semibold text-emerald-800 text-base">Import Successful</div>
                  <div className="text-sm text-emerald-700 mt-0.5">Month: <strong>{importResult.month}</strong></div>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="border rounded-md p-3 bg-slate-50 text-center">
                  <div className="text-2xl font-display font-extrabold text-[#1F7A6E]" data-testid="wizard-result-cells">{importResult.cells_updated}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Cells Written</div>
                </div>
                <div className="border rounded-md p-3 bg-slate-50 text-center">
                  <div className="text-2xl font-display font-extrabold text-emerald-700" data-testid="wizard-result-emps">{importResult.employees_matched}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Employees</div>
                </div>
                {importResult.cells_skipped_conflicts > 0 && (
                  <div className="border border-teal-200 rounded-md p-3 bg-teal-50 text-center">
                    <div className="text-2xl font-display font-extrabold text-teal-700">{importResult.cells_skipped_conflicts}</div>
                    <div className="text-[10px] uppercase tracking-widest text-teal-600 mt-0.5">Skipped (conflict)</div>
                  </div>
                )}
                {importResult.comp_off_earned > 0 && (
                  <div className="border border-amber-200 rounded-md p-3 bg-amber-50 text-center">
                    <div className="text-2xl font-display font-extrabold text-amber-700">{importResult.comp_off_earned}</div>
                    <div className="text-[10px] uppercase tracking-widest text-amber-600 mt-0.5">Comp-Offs Earned</div>
                  </div>
                )}
              </div>
              {importResult.month_mismatch && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  File was for <strong>{importResult.month_from_file}</strong> but imported into <strong>{importResult.month}</strong> per your selection.
                </div>
              )}
              {importResult.skipped_codes?.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-slate-600 mb-1.5">Skipped Emp Codes (not in employee master):</div>
                  <div className="flex flex-wrap gap-1.5">
                    {importResult.skipped_codes.map(c => (
                      <span key={c} className="text-[11px] font-mono bg-slate-100 text-slate-600 border border-slate-200 rounded px-2 py-0.5">{c}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 flex justify-between items-center shrink-0">
          {step === 0 && (
            <>
              <Button variant="outline" onClick={onClose} data-testid="wizard-cancel">Cancel</Button>
              <Button onClick={analyzeFile} disabled={!file || loading} className="bg-[#1F7A6E] hover:bg-[#14504E] disabled:opacity-50" data-testid="wizard-analyze-btn">
                {loading && <Loader2 size={14} className="animate-spin mr-2" />}
                {loading ? 'Analyzing…' : 'Analyze File →'}
              </Button>
            </>
          )}
          {step === 1 && (
            <>
              <Button variant="outline" onClick={() => setStep(0)} data-testid="wizard-back">← Back</Button>
              <Button onClick={doImport} disabled={!preview?.header_found || importing} className="bg-[#1F7A6E] hover:bg-[#14504E] disabled:opacity-50" data-testid="wizard-import-btn">
                {importing && <Loader2 size={14} className="animate-spin mr-2" />}
                {!importing && <Upload size={14} className="mr-2" />}
                {importing ? 'Importing…' : `Import ${summary.valid_cells || 0} cells into ${month}`}
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <div />
              <Button onClick={onClose} className="bg-[#1F7A6E] hover:bg-[#14504E]" data-testid="wizard-done-btn">
                <Check size={14} className="mr-2" /> Done
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function LeaveWorkflow() {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showApply, setShowApply] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState('');
  const [fDept, setFDept] = useState('');
  const [fProj, setFProj] = useState('');
  const [fLoc, setFLoc] = useState('');
  const [fQuery, setFQuery] = useState('');
  const [form, setForm] = useState({ employee_id: '', leave_type: 'CL', from_date: '', to_date: '', reason: '', medical_cert_url: '' });
  const [policy, setPolicy] = useState(null);
  const [balance, setBalance] = useState(null);
  const [uploadingCert, setUploadingCert] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/hr/leaves`, { credentials: 'include' });
      const d = await r.json();
      setLeaves(d || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetch(`${API}/api/hr/employees`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setEmployees(d || []))
      .catch(() => {});
    fetch(`${API}/api/hr/leaves/policy`, { credentials: 'include' })
      .then(r => r.json()).then(d => setPolicy(d)).catch(() => {});
  }, [fetchData]);

  useEffect(() => {
    if (!form.employee_id) { setBalance(null); return; }
    fetch(`${API}/api/hr/leaves/balance?employee_id=${form.employee_id}`, { credentials: 'include' })
      .then(r => r.json()).then(setBalance).catch(() => setBalance(null));
  }, [form.employee_id]);

  const previewDays = (() => {
    if (!form.from_date || !form.to_date) return 0;
    const a = new Date(form.from_date), b = new Date(form.to_date);
    return Math.max(0, Math.floor((b - a) / 86400000) + 1);
  })();
  const approverPreview = previewDays <= 0 ? null
    : previewDays <= 2 ? 'Reporting Manager'
    : previewDays <= 5 ? 'Department Head'
    : 'Director / CEO';

  const typeBalance = balance?.balance?.find((b) => b.leave_type === form.leave_type);
  const slNeedsCert = form.leave_type === 'SL' && previewDays > 2;

  const uploadCert = async (file) => {
    if (!file) return;
    setUploadingCert(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API}/api/hr/leaves/upload-medical-cert`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Upload failed');
      setForm(f => ({ ...f, medical_cert_url: data.url }));
      toast.success('Medical certificate uploaded');
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploadingCert(false);
    }
  };

  const apply = async () => {
    if (!form.employee_id || !form.from_date || !form.to_date) {
      toast.error('Employee and dates required');
      return;
    }
    const r = await fetch(`${API}/api/hr/leaves`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(form),
    });
    if (r.ok) {
      toast.success('Leave request submitted');
      setShowApply(false);
      setForm({ employee_id: '', leave_type: 'CL', from_date: '', to_date: '', reason: '', medical_cert_url: '' });
      fetchData();
    } else {
      const err = await r.json().catch(() => ({}));
      toast.error(err.detail || 'Failed to apply');
    }
  };

  const decide = async (leaveId, action) => {
    const r = await fetch(`${API}/api/hr/leaves/${leaveId}/${action}`, {
      method: 'PUT',
      credentials: 'include',
    });
    if (r.ok) {
      toast.success(`Leave ${action === 'approve' ? 'approved' : 'rejected'}`);
      fetchData();
    } else {
      toast.error(`Failed to ${action}`);
    }
  };

  const depts = Array.from(new Set(leaves.map(l => l.department).filter(Boolean))).sort();
  const projs = Array.from(
    new Map(
      leaves
        .filter(l => l.project_id)
        .map(l => [l.project_id, { id: l.project_id, name: l.project_name || l.project_id }])
    ).values()
  ).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const locs = Array.from(new Set(leaves.map(l => l.work_location).filter(Boolean))).sort();

  const filtered = leaves.filter(l => {
    if (filter && l.status !== filter) return false;
    if (fDept && (l.department || '') !== fDept) return false;
    if (fProj && (l.project_id || '') !== fProj) return false;
    if (fLoc && (l.work_location || '') !== fLoc) return false;
    if (fQuery) {
      const q = fQuery.toLowerCase();
      const hay = `${l.employee_name || ''} ${l.emp_code || ''} ${l.reason || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  const clearAdvFilters = () => { setFDept(''); setFProj(''); setFLoc(''); setFQuery(''); };
  const hasAdvFilter = !!(fDept || fProj || fLoc || fQuery);

  const exportFilteredCsv = () => {
    if (filtered.length === 0) { toast.error('No rows to export'); return; }
    const header = ['Employee', 'Emp Code', 'Department', 'Project', 'Location', 'Leave Type', 'From', 'To', 'Days', 'Status', 'Reason'];
    const csvRows = [
      header.join(','),
      ...filtered.map(l => [
        l.employee_name, l.emp_code, l.department, l.project_name, l.work_location,
        l.leave_type, l.from_date, l.to_date, l.days, l.status, (l.reason || '').replace(/\n/g, ' ')
      ].map(v => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(','))
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leave-workflow-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} row${filtered.length === 1 ? '' : 's'}`);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          {['', 'Pending', 'Approved', 'Rejected'].map(s => (
            <button
              key={s || 'all'}
              onClick={() => setFilter(s)}
              data-testid={`leave-filter-${s || 'all'}`}
              className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${
                filter === s ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {s || 'All'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportFilteredCsv}
            data-testid="leave-export-csv"
            className="text-xs px-3 py-1.5 rounded-sm border border-emerald-300 text-emerald-700 hover:bg-emerald-50 flex items-center gap-1.5"
          >
            <Download size={12} /> Export ({filtered.length})
          </button>
          <Button onClick={() => setShowApply(true)} className="bg-[#1F7A6E] hover:bg-[#14504E]" data-testid="apply-leave-btn">
            <Plus size={14} className="mr-1.5" /> Apply Leave
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="leave-filter-row">
        <input
          type="text"
          value={fQuery}
          onChange={(e) => setFQuery(e.target.value)}
          placeholder="Search name / emp-code / reason…"
          className="border border-slate-200 rounded-md px-3 py-1.5 text-xs w-56 focus:outline-none focus:border-[#1F7A6E]"
          data-testid="leave-filter-search"
        />
        <select
          value={fDept}
          onChange={(e) => setFDept(e.target.value)}
          className="border border-slate-200 rounded-md px-3 py-1.5 text-xs bg-white"
          data-testid="leave-filter-department"
        >
          <option value="">All Departments ({depts.length})</option>
          {depts.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select
          value={fProj}
          onChange={(e) => setFProj(e.target.value)}
          className="border border-slate-200 rounded-md px-3 py-1.5 text-xs bg-white max-w-[260px]"
          data-testid="leave-filter-project"
        >
          <option value="">All Projects ({projs.length})</option>
          {projs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select
          value={fLoc}
          onChange={(e) => setFLoc(e.target.value)}
          className="border border-slate-200 rounded-md px-3 py-1.5 text-xs bg-white"
          data-testid="leave-filter-location"
        >
          <option value="">All Locations ({locs.length})</option>
          {locs.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        {hasAdvFilter && (
          <button onClick={clearAdvFilters} className="text-[11px] text-slate-500 hover:text-rose-600 underline ml-1" data-testid="leave-filter-clear">
            Clear filters
          </button>
        )}
        <span className="text-[11px] text-slate-500 ml-auto" data-testid="leave-filter-count">
          <span className="font-semibold text-slate-700">{filtered.length}</span> of {leaves.length} requests
        </span>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
            <table className="w-full text-sm sticky-col-header">
              <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-display uppercase tracking-[0.14em] text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2.5">Employee</th>
                  <th className="text-left px-3 py-2.5">Type</th>
                  <th className="text-left px-3 py-2.5">From</th>
                  <th className="text-left px-3 py-2.5">To</th>
                  <th className="text-center px-3 py-2.5">Days</th>
                  <th className="text-left px-3 py-2.5">Reason</th>
                  <th className="text-center px-3 py-2.5">Status</th>
                  <th className="text-right px-3 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-8 text-slate-500">Loading…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-8 text-slate-500">No leave records.</td></tr>
                ) : filtered.map(l => (
                  <tr key={l.id} className="border-b border-slate-100" data-testid={`leave-row-${l.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium">{l.employee_name}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{l.emp_code}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-[10px] font-bold px-2 py-0.5 bg-slate-100 rounded-sm">{l.leave_type}</span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{l.from_date}</td>
                    <td className="px-3 py-2 font-mono text-xs">{l.to_date}</td>
                    <td className="px-3 py-2 text-center font-bold">{l.days}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs max-w-xs truncate" title={l.reason}>{l.reason || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] px-2 py-0.5 rounded-sm font-bold ${
                        l.status === 'Approved' ? 'bg-emerald-50 text-emerald-700' :
                        l.status === 'Rejected' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'
                      }`}>{l.status}</span>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {l.status === 'Pending' ? (
                        <>
                          <button onClick={() => decide(l.id, 'approve')} className="text-emerald-600 hover:bg-emerald-50 p-1 rounded mr-1" data-testid={`approve-${l.id}`} title="Approve">
                            <Check size={14} />
                          </button>
                          <button onClick={() => decide(l.id, 'reject')} className="text-red-600 hover:bg-red-50 p-1 rounded" data-testid={`reject-${l.id}`} title="Reject">
                            <X size={14} />
                          </button>
                        </>
                      ) : (
                        <span className="text-[10px] text-slate-400">{l.approver || '—'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {showApply && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-md w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="leave-apply-modal">
            <div className="flex items-center justify-between border-b px-5 py-3">
              <h3 className="font-display font-bold text-lg">Apply for Leave</h3>
              <button onClick={() => setShowApply(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <Field label="Employee">
                <select value={form.employee_id} onChange={(e) => setForm(f => ({ ...f, employee_id: e.target.value }))} className={inp}>
                  <option value="">Select employee</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{`${e.emp_id} · ${e.name}`}</option>)}
                </select>
              </Field>
              <Field label="Leave Type">
                <select value={form.leave_type} onChange={(e) => setForm(f => ({ ...f, leave_type: e.target.value, medical_cert_url: '' }))} className={inp} data-testid="leave-type-select">
                  {policy?.policy && Object.entries(policy.policy).map(([code, p]) => (
                    <option key={code} value={code}>{p.name} ({code})</option>
                  ))}
                  {!policy && (
                    <>
                      <option value="CL">Casual Leave (CL)</option>
                      <option value="SL">Sick Leave (SL)</option>
                      <option value="PL">Privilege Leave (PL)</option>
                      <option value="LWP">Leave Without Pay (LWP)</option>
                    </>
                  )}
                </select>
                {typeBalance && (
                  <div className={`mt-1 text-[11px] ${typeBalance.eligible ? 'text-slate-500' : 'text-amber-700'}`}>
                    {typeBalance.eligible ? (
                      <>Quota {typeBalance.used}/{typeBalance.quota} used · <strong>{typeBalance.remaining}</strong> {typeBalance.unit || 'days'} left</>
                    ) : (
                      <>⚠ {typeBalance.ineligibility_reason}</>
                    )}
                  </div>
                )}
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="From"><input type="date" value={form.from_date} onChange={(e) => setForm(f => ({ ...f, from_date: e.target.value }))} className={inp} /></Field>
                <Field label="To"><input type="date" value={form.to_date} onChange={(e) => setForm(f => ({ ...f, to_date: e.target.value }))} className={inp} /></Field>
              </div>
              <Field label="Reason">
                <textarea rows={2} value={form.reason} onChange={(e) => setForm(f => ({ ...f, reason: e.target.value }))} className={inp} />
              </Field>

              {slNeedsCert && (
                <Field label={`Medical Certificate · required for SL > 2 days${form.medical_cert_url ? ' ✓' : ''}`}>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => uploadCert(e.target.files?.[0])}
                    disabled={uploadingCert}
                    data-testid="leave-medical-cert"
                    className="text-xs"
                  />
                  {form.medical_cert_url && (
                    <div className="text-[11px] text-emerald-700 font-mono mt-1 truncate">{form.medical_cert_url.split('/').pop()}</div>
                  )}
                </Field>
              )}

              {approverPreview && (
                <div className="text-[11px] bg-teal-50 border border-teal-200 text-teal-900 rounded-sm px-3 py-2" data-testid="leave-approver-preview">
                  <strong>{previewDays} day{previewDays > 1 ? 's' : ''}</strong> · This request will be routed to <strong>{approverPreview}</strong>.
                  <span className="block text-[10px] text-teal-700 mt-0.5">Sandwich rule may further reduce chargeable days when weekends/holidays fall inside the range.</span>
                </div>
              )}
            </div>
            <div className="border-t px-5 py-3 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowApply(false)}>Cancel</Button>
              <Button onClick={apply} className="bg-[#1F7A6E] hover:bg-[#14504E]" data-testid="leave-submit-btn">Submit</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = "w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F7A6E]/40";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] text-slate-600 mb-1 block">{label}</label>
      {children}
    </div>
  );
}
